import React, { useState, useEffect } from 'react';
import { GitMerge, RefreshCw, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { DuplicateCluster } from '@/services/kbService';
import { QAErrorAlert } from './QAErrorAlert';

export interface QADuplicateClustersTabProps {
  clusters: DuplicateCluster[];
  totalClusters: number;
  totalDuplicateEntities: number;
  loading: boolean;
  error: string | null;
  validEntityTypes: string[];
  onMergeCluster: (entityIds: string[], primaryId: string, targetType?: string) => Promise<void>;
  onRefresh: () => void;
}

const TYPE_COLORS: Record<string, string> = {
  PLANT: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  SERVICE: 'bg-primary/10 text-primary border-primary/30',
  MATERIAL: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  EQUIPMENT: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  CHEMICAL: 'bg-destructive/10 text-red-400 border-destructive/30',
  PERSON: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
  ORGANIZATION: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
  LOCATION: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
  GPE: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
  REGULATION: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  CERTIFICATION: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  MEASUREMENT: 'bg-secondary text-muted-foreground border-border',
};

function getTypeColor(type: string): string {
  return TYPE_COLORS[type] || 'bg-secondary text-muted-foreground border-border';
}

export const QADuplicateClustersTab: React.FC<QADuplicateClustersTabProps> = ({
  clusters,
  totalClusters,
  totalDuplicateEntities,
  loading,
  error,
  validEntityTypes,
  onMergeCluster,
  onRefresh,
}) => {
  // Track expanded state per cluster - default expand first 3
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());

  // Track selected primary entity per cluster
  const [selectedPrimary, setSelectedPrimary] = useState<Record<string, string>>({});

  // Track target type override per cluster
  const [targetTypes, setTargetTypes] = useState<Record<string, string>>({});

  // Track merging state per cluster
  const [mergingClusters, setMergingClusters] = useState<Set<string>>(new Set());

  // Auto-expand first 3 clusters when data loads
  useEffect(() => {
    if (clusters.length > 0) {
      const initial = new Set<string>();
      clusters.slice(0, 3).forEach((c) => initial.add(c.cluster_id));
      setExpandedClusters(initial);
    }
  }, [clusters]);

  const toggleExpand = (clusterId: string) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) {
        next.delete(clusterId);
      } else {
        next.add(clusterId);
      }
      return next;
    });
  };

  const getPrimaryId = (cluster: DuplicateCluster): string => {
    return selectedPrimary[cluster.cluster_id] || cluster.entities[0]?.id || '';
  };

  const getTargetType = (cluster: DuplicateCluster): string => {
    if (targetTypes[cluster.cluster_id]) return targetTypes[cluster.cluster_id];
    const primaryId = getPrimaryId(cluster);
    const primary = cluster.entities.find((e) => e.id === primaryId);
    return primary?.entity_type || cluster.entities[0]?.entity_type || '';
  };

  const handleMerge = async (cluster: DuplicateCluster) => {
    const entityIds = cluster.entities.map((e) => e.id);
    const primaryId = getPrimaryId(cluster);
    const targetType = targetTypes[cluster.cluster_id]; // Only pass if explicitly overridden

    setMergingClusters((prev) => new Set(prev).add(cluster.cluster_id));
    try {
      await onMergeCluster(entityIds, primaryId, targetType);
    } finally {
      setMergingClusters((prev) => {
        const next = new Set(prev);
        next.delete(cluster.cluster_id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="sm" fullScreen={false} message="Loading duplicate clusters..." />
      </div>
    );
  }

  if (error) {
    return <QAErrorAlert message="Failed to load duplicate clusters" error={error} onRetry={onRefresh} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-purple-400" />
            Duplicate Clusters
            {totalClusters > 0 && (
              <Badge variant="secondary">{totalClusters}</Badge>
            )}
          </h3>
          {totalClusters > 0 && (
            <p className="text-sm text-muted-foreground">
              {totalClusters} cluster{totalClusters !== 1 ? 's' : ''} containing{' '}
              {totalDuplicateEntities} duplicate entit{totalDuplicateEntities !== 1 ? 'ies' : 'y'}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Empty State */}
      {clusters.length === 0 && (
        <Card className="p-8 text-center">
          <GitMerge className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium text-muted-foreground">No Duplicate Clusters Found</p>
          <p className="text-sm text-muted-foreground mt-1">
            All canonical entities have unique normalized text. No merging needed.
          </p>
        </Card>
      )}

      {/* Cluster Cards */}
      {clusters.map((cluster) => {
        const isExpanded = expandedClusters.has(cluster.cluster_id);
        const isMerging = mergingClusters.has(cluster.cluster_id);
        const primaryId = getPrimaryId(cluster);

        return (
          <Card key={cluster.cluster_id} className="overflow-hidden">
            {/* Cluster Header - Clickable */}
            <button
              type="button"
              className="flex items-center justify-between w-full p-4 cursor-pointer hover:bg-muted/50 transition-colors text-left"
              onClick={() => toggleExpand(cluster.cluster_id)}
              aria-expanded={isExpanded}
              aria-label={`Cluster: ${cluster.normalized_text}, ${cluster.entity_count} entities`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">
                      &ldquo;{cluster.normalized_text}&rdquo;
                    </span>
                    <Badge variant="outline" className="flex-shrink-0">
                      {cluster.entity_count} entities
                    </Badge>
                    {cluster.is_cross_type && (
                      <Badge variant="destructive" className="flex-shrink-0 text-xs">
                        Cross-type
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex gap-1 flex-wrap">
                      {cluster.types_found.map((type) => (
                        <Badge
                          key={type}
                          variant="outline"
                          className={`text-xs ${getTypeColor(type)}`}
                        >
                          {type}
                        </Badge>
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {cluster.total_occurrences} occurrences &middot; {cluster.total_documents}{' '}
                      documents
                    </span>
                  </div>
                </div>
              </div>
            </button>

            {/* Cluster Body - Expanded */}
            {isExpanded && (
              <div className="border-t px-4 pb-4">
                {/* Entity list - mobile cards */}
                <div className="md:hidden mt-3 space-y-2">
                  {cluster.entities.map((entity) => (
                    <div
                      key={entity.id}
                      className={`border rounded-lg p-3 space-y-2 ${
                        entity.id === primaryId ? 'bg-purple-500/10 border-purple-500/30' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="radio"
                          name={`primary-mobile-${cluster.cluster_id}`}
                          checked={entity.id === primaryId}
                          onChange={() =>
                            setSelectedPrimary((prev) => ({
                              ...prev,
                              [cluster.cluster_id]: entity.id,
                            }))
                          }
                          className="h-4 w-4 mt-0.5 text-purple-400 cursor-pointer flex-shrink-0"
                          aria-label={`Select ${entity.canonical_text} as primary`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium break-words">
                            {entity.canonical_text}
                          </p>
                          <Badge
                            variant="outline"
                            className={`mt-1 text-[10px] ${getTypeColor(entity.entity_type)}`}
                          >
                            {entity.entity_type}
                          </Badge>
                        </div>
                        <span className="text-xs font-mono tabular-nums text-muted-foreground flex-shrink-0">
                          {(entity.confidence_score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground pl-6">
                        <span>
                          <span className="tabular-nums font-medium text-foreground">
                            {entity.occurrence_count}
                          </span>{' '}
                          occurrences
                        </span>
                        <span>
                          <span className="tabular-nums font-medium text-foreground">
                            {entity.document_count}
                          </span>{' '}
                          documents
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Entity Table - desktop */}
                <div className="hidden md:block overflow-x-auto mt-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 pr-3 w-10 font-medium text-muted-foreground">
                          Primary
                        </th>
                        <th className="pb-2 pr-3 font-medium text-muted-foreground">
                          Canonical Text
                        </th>
                        <th className="pb-2 pr-3 font-medium text-muted-foreground">Type</th>
                        <th className="pb-2 pr-3 text-right font-medium text-muted-foreground">
                          Occurrences
                        </th>
                        <th className="pb-2 pr-3 text-right font-medium text-muted-foreground">
                          Documents
                        </th>
                        <th className="pb-2 text-right font-medium text-muted-foreground">
                          Confidence
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cluster.entities.map((entity) => (
                        <tr
                          key={entity.id}
                          className={`border-b last:border-b-0 ${
                            entity.id === primaryId ? 'bg-purple-500/10' : ''
                          }`}
                        >
                          <td className="py-2 pr-3">
                            <input
                              type="radio"
                              name={`primary-${cluster.cluster_id}`}
                              checked={entity.id === primaryId}
                              onChange={() =>
                                setSelectedPrimary((prev) => ({
                                  ...prev,
                                  [cluster.cluster_id]: entity.id,
                                }))
                              }
                              className="h-4 w-4 text-purple-400 cursor-pointer"
                              aria-label={`Select ${entity.canonical_text} as primary`}
                            />
                          </td>
                          <td className="py-2 pr-3 font-medium">{entity.canonical_text}</td>
                          <td className="py-2 pr-3">
                            <Badge
                              variant="outline"
                              className={`text-xs ${getTypeColor(entity.entity_type)}`}
                            >
                              {entity.entity_type}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {entity.occurrence_count}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {entity.document_count}
                          </td>
                          <td className="py-2 text-right tabular-nums">
                            {(entity.confidence_score * 100).toFixed(0)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Actions Bar */}
                <div className="flex flex-col gap-3 mt-4 pt-3 border-t md:flex-row md:items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      Target type:
                    </span>
                    <Select
                      value={getTargetType(cluster)}
                      onValueChange={(value: string) =>
                        setTargetTypes((prev) => ({
                          ...prev,
                          [cluster.cluster_id]: value,
                        }))
                      }
                    >
                      <SelectTrigger className="w-full md:w-[180px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark">
                        {validEntityTypes.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMerge(cluster)}
                    disabled={isMerging || cluster.entities.length < 2}
                    className="w-full md:w-auto"
                  >
                    {isMerging ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Merging...
                      </>
                    ) : (
                      <>
                        <GitMerge className="h-3 w-3 mr-1" />
                        Merge Cluster
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default QADuplicateClustersTab;
